import {Button} from "@/components/buttons/Button";
import {ModalBody} from "@/widgets/ModalParts";
import {Modal} from "@/widgets/Modal";
import { RewardFulfillmentClaimForm } from "@/components/rewards/RewardFulfillmentClaimForm";
import { addError } from "@/redux/meta/metaActions";
import { useConfirmNotifyPrizeWon } from "@/hooks/useConfirmNotifyPrizeWon";
import { PrizePool } from "@devour/client";
import { FC, useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { useDispatch } from "react-redux";

interface Props {
    prize: PrizePool;
    onAccepted: () => Promise<void>;
    isOpen: boolean;
}

export const BrandChallengePrizeWonModal: FC<Props> = ({ prize, isOpen, onAccepted }) => {
    const [isAccepting, setIsAccepting] = useState(false);
    const [shippingSubmitted, setShippingSubmitted] = useState(false);
    const dispatch = useDispatch();
    const confirmNotifyPrizeWon = useConfirmNotifyPrizeWon();

    /*
     * Shippable prizes render the inline RewardFulfillmentClaimForm
     * instead of going straight to the Confirm button — gated on the
     * admin-controlled `useInlineFulfillmentForm` flag on the
     * RewardConfig. Admins toggle this in the reward editor instead of
     * the form firing on a hardcoded type-check, so non-MERCH rewards
     * that need shipping (one-off promo packages, etc.) can opt in,
     * and physical MERCH that goes out via an external claim URL can
     * stay on that flow.
     */
    const requiresShipping = Boolean(prize?.rewardConfig?.useInlineFulfillmentForm);

    /*
     * Reset the inline-form's submitted state whenever the prize prop
     * swaps. A winner who claims prize A then has the modal reopen for
     * prize B should see a fresh shipping form, not a stale "Done"
     * screen carried over from the previous prize.
     */
    useEffect(() => {
        setShippingSubmitted(false);
    }, [prize?.id]);

    // Get challenge theme data from prize
    const challenge = prize?.challenge as any;
    const brandColor = challenge?.brandColor || challenge?.themeColors?.primary || "#7B7B7B";
    const themeColors = challenge?.themeColors || {};

    // Get prize image - first from imageUrls array, then fallback to rewardConfig asset
    const prizeImageUrl = (prize as any)?.imageUrls?.[0] || prize?.rewardConfig?.asset?.cdnUrl;
    const backgroundImageUrl = challenge?.backgroundImageLarge;
    const brandLogoUrl = challenge?.brandLogo;

    const handleAccept = async () => {
        /*
         * Re-entrancy guard — close button, overlay dismiss, and the
         * Confirm button all route here. Without the early-return, a
         * user who taps outside the modal (overlay isn't disabled)
         * while the first confirm-notify mutation is still in flight
         * fires a second concurrent mutation. Mirrors the guard used
         * in MinelabRttrPrizeWonModal.
         */
        if (isAccepting) {
            return;
        }
        /*
         * Defensive null-guard — `prize` is typed non-optional but the
         * overlay-dismiss path can fire `handleAccept` before the
         * parent fully populates `prize` in race conditions. Skipping
         * the mutation and calling `onAccepted()` directly matches the
         * MinelabRttrPrizeWonModal guard.
         */
        if (!prize?.id) {
            await onAccepted();
            return;
        }
        setIsAccepting(true);
        try {
            await confirmNotifyPrizeWon.mutateAsync(prize.id);
            await onAccepted();
        } catch (e) {
            dispatch(await addError(e));
        } finally {
            setIsAccepting(false);
        }
    };

    const dateMintedString = challenge?.end
        ? new Date(challenge.end).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        })
        : "";

    // CSS variables for theming
    const themeStyle = {
        "--brand-color": brandColor,
        "--challenge-primary": themeColors.primary || brandColor,
        "--challenge-primary-dark": themeColors.secondary || brandColor,
        "--challenge-text-primary": themeColors.textPrimary || "#FFFFFF",
        "--challenge-text-secondary": themeColors.textSecondary || "#A0A0A5",
        "--challenge-text-muted": themeColors.textMuted || "#87878D",
    } as React.CSSProperties;

    return (
        <Modal
            contentClassName="brand-challenge_prize-modal"
            size="xs1"
            isOpen={isOpen}
            disableOverlayDismiss={false}
            toggle={handleAccept}
            style={themeStyle}
        >
            {prize && <>
                {/* Background image with gradient overlay - matches accept/invite modal pattern */}
                {backgroundImageUrl &&
                    <div
                        className="brand-challenge_prize-modal_background"
                        style={{
                            backgroundImage: `url(${backgroundImageUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    />
                }
                <div
                    className="brand-challenge_prize-modal_shader"
                    style={{
                        background: "linear-gradient(180deg, rgba(45, 45, 47, 0.10) 0%, #2D2D2F 31.33%)",
                    }}
                />
                <button
                    className="brand-challenge_prize-modal_close"
                    onClick={handleAccept}
                    aria-label="Close modal"
                    type="button"
                    disabled={isAccepting}
                >
                    <IoMdClose />
                </button>

                <ModalBody className="brand-challenge_prize-modal_body">
                    {/* Brand logo at top - matches accept/invite modal */}
                    <div className="brand-challenge_prize-modal_body_heading">
                        {brandLogoUrl &&
                            <img src={brandLogoUrl} alt="Brand Logo" className="brand-challenge_prize-modal_body_heading_logo" />
                        }
                    </div>

                    {/* Prize image */}
                    {prizeImageUrl &&
                        <div className="brand-challenge_prize-modal_body_prize-image">
                            <img src={prizeImageUrl} alt="Prize" />
                        </div>
                    }

                    {/* Content section with glow effect */}
                    <div className="brand-challenge_prize-modal_body_content">
                        <h6 style={{
                            textShadow: `0px 0px 24px ${brandColor}, 0px 0px 8px ${brandColor}`,
                        }}>
                            {prize?.rewardConfig?.name}
                        </h6>
                        <div className="brand-challenge_prize-modal_body_content_claimed">
                            <FaCheckCircle style={{ color: brandColor }} /> Claimed
                        </div>
                        <span className="brand-challenge_prize-modal_body_content_date">
                            Claimed on {dateMintedString}
                        </span>
                        <p>
                            Congratulations! You have won the above prize in the {challenge?.name}. {prize?.winnerMessage}
                        </p>
                    </div>

                    {requiresShipping && !shippingSubmitted &&
                        <RewardFulfillmentClaimForm
                            prizePoolId={prize.id}
                            rewardName={prize?.rewardConfig?.name ?? "your prize"}
                            className="brand-challenge_prize-modal_body_fulfillment-form"
                            onSubmitted={() => setShippingSubmitted(true)}
                        />
                    }

                    {/* Action button with brand color — for non-shippable prizes
                        OR after shipping submission, lets the user dismiss + ack. */}
                    {(!requiresShipping || shippingSubmitted) &&
                        <Button variant="primary"
                            onClick={handleAccept}
                            loading={isAccepting}


                            {...{
                                style: { backgroundColor: brandColor, width: "100%"},
                            }}
                        >
                            {shippingSubmitted ? "Done" : "Confirm"}
                        </Button>
                    }
                </ModalBody>
            </>}
        </Modal>
    );
};
